<template>
  <div class="lpro-contact-form">
    <template v-if="!formSuccess">
      <div class="sitecore-form-component">
        <p class="lpro-contact-form__instructions">
          {{ formMandatoryInstruction }}
        </p>
        <slot name="form">Form Here</slot>
      </div>
    </template>
    <template v-else>
      <div class="lpro-contact-form-success">
        <h1 class="lpro-contact-form-success__title uppercase">
          {{ confirmTitlePrimary }}
          <br>
          {{ confirmTitleSecondary }}
        </h1>
        <p class="lpro-contact-form-success__message">{{ confirmMessage }}</p>
        <a
          href="/"
          class="button -primary return-back"
          @click="pushReturnTag"
          >{{ confirmButtonText }}</a
        >
      </div>
    </template>
  </div>
</template>

<script>
import { parseSitecoreForm, bindFormField } from 'Utils/form';
import { pushEvent } from 'Utils/datalayer';
import { mixinFormRecaptcha } from 'Mixins/formRecaptcha/formRecaptcha';

export default {
  name: 'contact-form',
  mixins: [mixinFormRecaptcha],
  props: {
    formMandatoryInstruction: {
      type: String,
      required: true,
    },
    confirmTitlePrimary: {
      type: String,
      required: true,
    },
    confirmTitleSecondary: {
      type: String,
      required: true,
    },
    confirmMessage: {
      type: String,
      required: true,
    },
    confirmButtonText: {
      type: String,
      required: true,
    },
    errorMessageFormIncomplete: {
      type: String,
      required: false,
      default: 'Please fill in all required fields.',
    },
    errorMessageFormTerms: {
      type: String,
      required: false,
      default: 'You must agree to the terms to continue.',
    },
    defaultErrorMessage: {
      type: String,
      required: false,
      default: 'An error occured while processing your request.',
    },
    testHost: {
      type: String,
      required: false,
      default: 'https://wsf-master-lp.cm',
    },
    submitButtonTagging: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      formSuccess: false,
      fields: [],
      formProperties: null,
      parser: new DOMParser(),
      errorWrapperNode: null,
      eventTriggered: false,
    };
  },
  computed: {
    formActionUrl() {
      return process.env.NODE_ENV === 'production'
        ? this.formProperties.formAction
        : `${this.testHost}${this.formProperties.formAction}`;
    },
  },
  mounted() {
    const formNode = this.$slots.form[0];
    if (!formNode?.elm) {
      return;
    }

    // Collect the base form properties and hijack form submit
    this.formProperties = parseSitecoreForm(formNode?.elm);
    formNode.elm.addEventListener('submit', this.onSubmit);

    // Create the error message wrapper
    this.errorWrapperNode = document.createElement('div');
    this.errorWrapperNode.setAttribute('role', 'status');
    this.errorWrapperNode.classList.add('lpro-contact-form__errorSummary');
    const submitButton = formNode?.elm.querySelector('input[type="submit"]');
    submitButton.parentNode.insertBefore(this.errorWrapperNode, submitButton);

    // Traverse the form and collect all the inputs into a single list
    formNode.children
      .filter((node) => node.tag === 'div' || node.tag === 'input')
      .forEach((node) => {
        const foundFieldInfo = bindFormField(node);
        if (foundFieldInfo.nestedFieldInfo.length) {
          this.fields.push(...foundFieldInfo.nestedFieldInfo);
        }
        this.fields.push(foundFieldInfo);
      });
  },
  methods: {
    async onSubmit(e) {
      e.preventDefault();
      if (!this.validateFields()) {
        return;
      }

      const submitButton = document.querySelector('.lpro-form-submit');
      if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
      }

      try {
        const canSubmit = await this.executeRecaptcha();
        if (canSubmit) {
          await this.submitForm();
        }
      } catch (error) {
        this.setErrorMessage(this.defaultErrorMessage);
      } finally {
        if (submitButton) {
          submitButton.removeAttribute('disabled');
        }
      }
    },

    async executeRecaptcha() {
      try {
        return await this.peformRecaptcha();
      } catch (error) {
        this.setErrorMessage(this.defaultErrorMessage);
        throw error;
      }
    },

    async submitForm() {
      try {
        const response = await fetch(this.formActionUrl, {
          method: 'POST',
          mode: process.env.NODE_ENV === 'production' ? 'cors' : 'no-cors',
          body: this.assembleFormData(),
        });

        if (response.status >= 400 && response.status < 600) {
          throw new Error(this.defaultErrorMessage);
        }

        const htmlResponse = this.parser.parseFromString(response, 'text/xml');
        const errorWrapper = htmlResponse.querySelector(
          '.validation-summary-errors'
        );

        if (errorWrapper) {
          this.handleServerErrors(errorWrapper);
        } else {
          this.handleSuccess();
        }
      } catch (error) {
        this.setErrorMessage(error.message);
        throw error;
      }
    },

    handleServerErrors(errorWrapper) {
      const serverErrorMsgs = Array.from(errorWrapper.querySelectorAll('li'));
      const summaryErrors = serverErrorMsgs.map((msg) => msg.textContent);
      this.setErrorMessage(summaryErrors);
    },

    handleSuccess() {
      this.formSuccess = true;
      this.pushSubmitTags();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },

    setErrorMessage(errorMessage) {
      this.errorWrapperNode.innerText = errorMessage;
    },

    validateFields() {
      const validationErrors = [];
      let hasTermsError = false;
      this.fields.forEach((field) => {
        const fieldErrors = field.validate();
        if (fieldErrors.length && field.isTermsAgree) {
          hasTermsError = true;
        }
        validationErrors.push(...fieldErrors);
      });
 
      if (hasTermsError) {
        this.setErrorMessage(this.errorMessageFormTerms);
        return false;
      } else if (validationErrors.length > 0) {
        this.setErrorMessage(this.errorMessageFormIncomplete);
        return false;
      }
      const errorSummary = document.querySelector('.lpro-contact-form__errorSummary');
      if (errorSummary) {
        errorSummary.innerHTML = '';
      }
      return true;
    },
    assembleFormData() {
      const formData = new FormData();
      this.fields.forEach((field) => {
        if (field.inputElement) {
          if (field.inputElement.type === 'file') {
            for (const file of field.inputElement.files) {
              formData.append(field.inputElement.name, file);
            }
          } else {
            formData.append(field.inputElement.name, field.inputElement.value);
          }
        }
        for (const hiddenField of field.hiddenFields) {
          if (hiddenField.name) {
            formData.append(hiddenField.name, hiddenField.value);
          }
        }
      });
      return formData;
    },
    pushSubmitTags() {
      if (!this.eventTriggered) {
        const submitTag = JSON.parse(this.submitButtonTagging);
        pushEvent(submitTag.eventProperties);
        this.eventTriggered = true;
      }
    },
    pushReturnTag() {
      pushEvent({
        event: 'uaevent',
        ecommerce: 'undefined',
        eventCategory: 'appendix',
        eventAction: 'select::lpro-contact-form',
        eventLabel: `${this.confirmButtonText.toLowerCase()}::${
          window.location.origin
        }`,
        event_name: 'body_button_click',
        cta_name: `${this.confirmButtonText.toLowerCase()}`,
        link_url: `${window.location.origin}`,
        module_name: 'lpro-contact-form',
      });
    },
  },
};
</script>

<style lang="scss" src="./contact-form.scss"></style>
